import { PANEL_TYPE } from "engage/routes/CompanySettings/components/TextPanels/constants";
import { checkFontLoaded } from "../../../../utils/SimulatedItemsUtility";

const recalculatePaddingWithPx = paddingString => {
    if (!paddingString) {
        return paddingString;
    }

    return String(paddingString)
        .trim()
        .split(/\s+/)
        .map(item => {
            const value = String(item).trim();
            if (!value) {
                return value;
            }
            return /px$/i.test(value) ? value : `${value}px`;
        })
        .join(' ');
};

const parseLabelStyle = async (labelObject, type) => {
    if (!labelObject) {
        return {};
    }

    try {
        await checkFontLoaded(labelObject.font);
    } catch (e) {
        console.log('Font loaded failed with error:', e);
    }

    return {
        borderStyle: 'solid',
        borderWidth: labelObject.borderWidth,
        borderColor: labelObject.borderColor,
        borderRadius: labelObject.borderRadius,
        borderRadiusTl: labelObject.borderRadiusTl,
        borderRadiusTr: labelObject.borderRadiusTr,
        borderRadiusBr: labelObject.borderRadiusBr,
        borderRadiusBl: labelObject.borderRadiusBl,
        opacity: labelObject.opacity,
        color: labelObject.fontColor,
        fontFamily: `'${labelObject.font.name}'`,
        fontSize: labelObject.fontSize,
        ...(type === PANEL_TYPE.TWO_PANELS.type && { padding: recalculatePaddingWithPx(labelObject.textPadding) }),
        ...(type === PANEL_TYPE.TWO_PANELS.type && { backgroundColor: labelObject.backgroundColor }),
    };
};

export const prepareTextPanelsList = async (list) => {
    return await Promise.all(
        list.map(async textPanel => {
            const onePanelType = textPanel.type === PANEL_TYPE.ONE_PANEL.type;

            const topLabel = onePanelType ? textPanel.labelTop : textPanel.panelTop;
            const bottomLabel = onePanelType ? textPanel.labelBottom : textPanel.panelBottom;

            const topLabelStyles = await parseLabelStyle(topLabel, textPanel.type);
            const bottomLabelStyles = await parseLabelStyle(bottomLabel, textPanel.type);

            const containerStyles = {};

            if (onePanelType) {
                containerStyles.padding = recalculatePaddingWithPx(textPanel.textPadding);
                containerStyles.backgroundColor = textPanel.bgColor;
                containerStyles.borderWidth = topLabelStyles.borderWidth;
                containerStyles.borderRadius = topLabelStyles.borderRadius;
                containerStyles.borderRadiusTl = topLabelStyles?.borderRadiusTl;
                containerStyles.borderRadiusTr = topLabelStyles?.borderRadiusTr;
                containerStyles.borderRadiusBr = topLabelStyles?.borderRadiusBr;
                containerStyles.borderRadiusBl = topLabelStyles?.borderRadiusBl;
                containerStyles.borderColor = topLabelStyles.borderColor;
                containerStyles.borderStyle = topLabelStyles.borderStyle;
                containerStyles.opacity = topLabelStyles.opacity / 100;

                topLabelStyles.borderWidth = 0;
                bottomLabelStyles.borderWidth = 0;
                delete topLabelStyles.borderRadius;
                delete topLabelStyles.borderColor;
            } else {
                bottomLabelStyles.opacity = topLabelStyles.opacity / 100;
            }

            topLabelStyles.marginBottom = textPanel.spacing;

            return { ...textPanel, topLabelStyles, bottomLabelStyles, containerStyles };
        })
    );
};

export const getPanelRadiusPerCorner = (panel) => {
    const perCornerRadius = ['borderRadiusTl', 'borderRadiusTr', 'borderRadiusBr', 'borderRadiusBl'].map(corner => panel[corner]);
    let useBorderRadius = perCornerRadius.some(corner => corner === null || corner === undefined);
    if (!useBorderRadius) {
        // Check for min corner radius value
        useBorderRadius = panel.borderRadius > Math.max(...perCornerRadius);
    }
    return [
        useBorderRadius ? panel.borderRadius : panel.borderRadiusTl,
        useBorderRadius ? panel.borderRadius : panel.borderRadiusTr,
        useBorderRadius ? panel.borderRadius : panel.borderRadiusBr,
        useBorderRadius ? panel.borderRadius : panel.borderRadiusBl,
    ];
}

export const recalculateTextPanel = (textPanel, recalculateVuToPx = (value) => value, recalculatePadding = recalculatePaddingWithPx) => {
    const template = structuredClone(textPanel);
    ['containerStyles', 'topLabelStyles', 'bottomLabelStyles'].forEach(field => {
        if (template[field]) {
            Object.entries(template[field]).forEach(([style, value]) => {
                if (!value) {
                    return;
                }
                template[field][style] = ['fontSize', 'borderWidth', 'marginBottom', 'borderRadius', 'borderRadiusTl', 'borderRadiusTr', 'borderRadiusBr', 'borderRadiusBl'].includes(style)
                    ? recalculateVuToPx(template[field][style])
                    : style === 'padding'
                        ? recalculatePadding(template[field][style])
                        : value;
            });
        }
        template[field].borderRadius = getPanelRadiusPerCorner(template[field]).map(radius => `${radius}px`).join(' ');
    });
    return template;
}